import React, { useState, useEffect, useMemo } from 'react';
import { View, FlatList, StyleSheet, ActivityIndicator, Text, TouchableOpacity, Platform, TextInput, Modal } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, UserWithClubs, Club } from '../types';
import { adminService } from '../services/admin';
import { clubsService } from '../services/clubs';
import { authService } from '../services/auth';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ManageUsers'>;

export const ManageUsersScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [users, setUsers] = useState<UserWithClubs[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<UserWithClubs | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    const isAdmin = await adminService.isCurrentUserAdmin();
    if (!isAdmin) {
      await dialog.alert(
        t('common.errors.unauthorized'),
        t('screens.adminDashboard.notAuthorized'),
        'error'
      );
      navigation.goBack();
      return;
    }

    const currentUser = await authService.getCurrentUser();
    if (currentUser) {
      setCurrentUserId(currentUser.id);
    }

    await loadData();
  };

  const loadData = async () => {
    try {
      const [usersData, clubsData] = await Promise.all([
        adminService.getAllUsers(),
        clubsService.getClubs(),
      ]);
      setUsers(usersData);
      setClubs(clubsData);
    } catch (error: any) {
      await dialog.alert(
        t('common.errors.error'),
        error.message,
        'error'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handlePromoteAdmin = async (user: UserWithClubs) => {
    const confirmed = await dialog.confirm(
      t('screens.manageUsers.promoteAdmin'),
      t('screens.manageUsers.confirmPromoteAdmin'),
      { variant: 'primary' }
    );

    if (confirmed) {
      try {
        await adminService.promoteToAdmin(user.id);
        await dialog.alert(
          t('common.success'),
          t('screens.manageUsers.successPromoteAdmin'),
          'success'
        );
        await loadData();
      } catch (error: any) {
        await dialog.alert(
          t('common.errors.error'),
          error.message,
          'error'
        );
      }
    }
  };

  const handleDemoteAdmin = async (user: UserWithClubs) => {
    if (user.id === currentUserId) {
      await dialog.alert(
        t('common.errors.error'),
        t('screens.manageUsers.cannotDemoteSelf'),
        'warning'
      );
      return;
    }

    const confirmed = await dialog.confirm(
      t('screens.manageUsers.demoteAdmin'),
      t('screens.manageUsers.confirmDemoteAdmin'),
      { variant: 'danger' }
    );

    if (confirmed) {
      try {
        await adminService.demoteFromAdmin(user.id);
        await dialog.alert(
          t('common.success'),
          t('screens.manageUsers.successDemoteAdmin'),
          'success'
        );
        await loadData();
      } catch (error: any) {
        await dialog.alert(
          t('common.errors.error'),
          error.message,
          'error'
        );
      }
    }
  };

  const handleDeleteUser = (user: UserWithClubs) => {
    setDeleteReason('');
    setDeleteTarget(user);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    setDeleting(true);
    try {
      await adminService.softDeleteUser(deleteTarget.id, deleteReason.trim());
      setDeleteTarget(null);
      await dialog.alert(
        t('screens.manageUsers.deleteSuccess'),
        t('screens.manageUsers.deleteSuccessMessage', {
          name: `${deleteTarget.first_name} ${deleteTarget.last_name}`.trim() || deleteTarget.email,
        }),
        'success'
      );
      await loadData();
    } catch (error: any) {
      await dialog.alert(t('common.errors.error'), error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleManageClubRole = async (user: UserWithClubs) => {
    // Simple implementation - could be enhanced with ClubPickerModal
    // For now, just show a placeholder
    await dialog.alert(
      t('screens.manageUsers.manageClubRole'),
      'Club role management coming soon',
      'info'
    );
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email.toLowerCase();
      const clubNames = user.club_memberships?.map(m => m.club?.name.toLowerCase()).join(' ') || '';

      return fullName.includes(query) || email.includes(query) || clubNames.includes(query);
    });
  }, [users, searchQuery]);

  const renderUser = ({ item: user }: { item: UserWithClubs }) => {
    const canDemote = user.id !== currentUserId;
    const isDeleted = user.is_deleted === true;
    const canDelete = !isDeleted && !user.is_platform_admin && user.id !== currentUserId;

    return (
      <View style={[styles.userCard, isDeleted && styles.userCardDeleted]}>
        <View style={styles.userInfo}>
          <Text style={styles.userName}>
            {user.first_name} {user.last_name}
            {user.is_platform_admin && ' 👑'}
          </Text>
          <Text style={styles.userEmail}>{user.email}</Text>
          {isDeleted && (
            <View style={styles.deletedBadge}>
              <Text style={styles.deletedBadgeText}>
                🗑 {t('screens.manageUsers.scheduledForDeletion')}
                {user.scheduled_hard_delete_at
                  ? ` – ${new Date(user.scheduled_hard_delete_at).toLocaleDateString()}`
                  : ''}
              </Text>
              {user.deletion_reason && (
                <Text style={styles.deletedReason}>
                  {t('screens.manageUsers.deletionReason')}: {user.deletion_reason}
                </Text>
              )}
            </View>
          )}
          {user.club_memberships && user.club_memberships.length > 0 && (
            <View style={styles.clubsContainer}>
              {user.club_memberships.map((membership, idx) => (
                <Text key={idx} style={styles.clubBadge}>
                  🏟️ {membership.club?.name}
                  {membership.role === 'manager' && ' (Manager)'}
                </Text>
              ))}
            </View>
          )}
        </View>
        {!isDeleted && (
          <View style={styles.actions}>
            {user.is_platform_admin ? (
              <TouchableOpacity
                style={[styles.button, styles.dangerButton, !canDemote && styles.disabledButton]}
                onPress={() => handleDemoteAdmin(user)}
                disabled={!canDemote}
              >
                <Text style={styles.buttonText}>
                  {t('screens.manageUsers.demoteAdmin')}
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.button, styles.primaryButton]}
                onPress={() => handlePromoteAdmin(user)}
              >
                <Text style={styles.buttonText}>
                  {t('screens.manageUsers.promoteAdmin')}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.button, styles.secondaryButton]}
              onPress={() => handleManageClubRole(user)}
            >
              <Text style={styles.buttonText}>
                {t('screens.manageUsers.manageClubRole')}
              </Text>
            </TouchableOpacity>
            {canDelete && (
              <TouchableOpacity
                style={[styles.button, styles.dangerButton]}
                onPress={() => handleDeleteUser(user)}
              >
                <Text style={styles.buttonText}>
                  {t('screens.manageUsers.deleteUser')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.manageUsers.title')}</Text>

          {/* Search Input */}
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={20} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('screens.manageUsers.searchPlaceholder')}
              value={searchQuery}
              onChangeText={setSearchQuery}
              clearButtonMode="while-editing"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                <Ionicons name="close-circle" size={20} color="#666" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      <FlatList
        data={filteredUsers}
        renderItem={renderUser}
        keyExtractor={(item) => item.id}
        contentContainerStyle={[
          styles.list,
          Platform.OS === 'web' && styles.listWeb,
        ]}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListEmptyComponent={
          <Text style={styles.emptyText}>
            {searchQuery ? t('screens.manageUsers.noSearchResults') : t('screens.manageUsers.noUsers')}
          </Text>
        }
      />

      {/* Delete user modal */}
      <Modal
        visible={deleteTarget !== null}
        transparent
        animationType="fade"
        onRequestClose={() => !deleting && setDeleteTarget(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('screens.manageUsers.deleteModalTitle')}</Text>
            <Text style={styles.modalSubtitle}>
              {t('screens.manageUsers.deleteModalSubtitle', {
                name: deleteTarget
                  ? `${deleteTarget.first_name} ${deleteTarget.last_name}`.trim() || deleteTarget.email
                  : '',
              })}
            </Text>
            <Text style={styles.modalInfo}>{t('screens.manageUsers.deleteModalInfo')}</Text>

            <Text style={styles.reasonLabel}>{t('screens.manageUsers.reasonLabel')}</Text>
            <TextInput
              style={styles.reasonInput}
              value={deleteReason}
              onChangeText={setDeleteReason}
              placeholder={t('screens.manageUsers.reasonPlaceholder')}
              multiline
              numberOfLines={3}
              editable={!deleting}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.button, styles.secondaryButton, { flex: 1 }]}
                onPress={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                <Text style={styles.buttonText}>{t('common.buttons.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.button,
                  styles.dangerButton,
                  { flex: 1 },
                  (!deleteReason.trim() || deleting) && styles.disabledButton,
                ]}
                onPress={handleConfirmDelete}
                disabled={!deleteReason.trim() || deleting}
              >
                {deleting
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={styles.buttonText}>{t('screens.manageUsers.deleteConfirm')}</Text>
                }
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerWeb: {
    alignItems: 'center',
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  backText: {
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    outlineStyle: undefined,
  },
  clearButton: {
    padding: 4,
  },
  list: {
    padding: 16,
  },
  listWeb: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 1200,
  },
  userCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  userInfo: {
    marginBottom: 12,
  },
  userName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  clubsContainer: {
    marginTop: 8,
  },
  clubBadge: {
    fontSize: 12,
    color: '#007AFF',
    marginBottom: 4,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  button: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: '#007AFF',
  },
  secondaryButton: {
    backgroundColor: '#8E8E93',
  },
  dangerButton: {
    backgroundColor: '#FF3B30',
  },
  disabledButton: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyText: {
    textAlign: 'center',
    color: '#666',
    fontSize: 16,
    marginTop: 32,
  },
  userCardDeleted: {
    opacity: 0.6,
    borderLeftWidth: 4,
    borderLeftColor: '#FF3B30',
  },
  deletedBadge: {
    marginTop: 6,
    backgroundColor: '#FEE2E2',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  deletedBadgeText: {
    fontSize: 12,
    color: '#B91C1C',
    fontWeight: '600',
  },
  deletedReason: {
    fontSize: 11,
    color: '#B91C1C',
    marginTop: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 480,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 6,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#333',
    marginBottom: 8,
  },
  modalInfo: {
    fontSize: 13,
    color: '#666',
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    padding: 10,
    marginBottom: 16,
    lineHeight: 18,
  },
  reasonLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: '#333',
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
});
